import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { Save, MapPin } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useForm } from "react-hook-form"
import { api, ApiRequestError } from "@/lib/api"
import type { ArealResponse } from "@fohet/caravan-booker-shared/types"
import { inputClass, labelClass, sectionClass, primaryBtnClass, sectionHeaderClass } from "@/lib/styleClasses"

interface LocationFormValues {
    address: string
    city: string
    zip: string
    country: string
    gpsLat: string
    gpsLng: string
    billingAddressDifferent: boolean
    billingCompanyName: string
    billingAddress: string
    billingCity: string
    billingZip: string
    billingCountry: string
    companyId: string
    companyVatId: string
}

export const LocationSection = ({ areal }: { areal: ArealResponse }) => {
    const { t } = useTranslation()
    const queryClient = useQueryClient()

    const { register, handleSubmit, getValues, setValue, watch, formState: { isDirty } } = useForm<LocationFormValues>({
        values: {
            address: areal.address,
            city: areal.city,
            zip: areal.zip ?? "",
            country: areal.country,
            gpsLat: areal.gpsLat?.toString() ?? "",
            gpsLng: areal.gpsLng?.toString() ?? "",
            billingAddressDifferent: areal.billingAddressDifferent,
            billingCompanyName: areal.billingCompanyName ?? "",
            billingAddress: areal.billingAddress ?? "",
            billingCity: areal.billingCity ?? "",
            billingZip: areal.billingZip ?? "",
            billingCountry: areal.billingCountry ?? "",
            companyId: areal.companyId ?? "",
            companyVatId: areal.companyVatId ?? "",
        },
    })

    const billingDifferent = watch("billingAddressDifferent")

    const saveMutation = useMutation({
        mutationFn: (values: LocationFormValues) =>
            api.patch<ArealResponse>(`/areals/${areal.id}`, {
                address: values.address,
                city: values.city,
                zip: values.zip || undefined,
                country: values.country,
                gpsLat: values.gpsLat ? Number(values.gpsLat.replace(",", ".")) : undefined,
                gpsLng: values.gpsLng ? Number(values.gpsLng.replace(",", ".")) : undefined,
                billingAddressDifferent: values.billingAddressDifferent,
                billingCompanyName: values.billingAddressDifferent ? (values.billingCompanyName || undefined) : undefined,
                billingAddress: values.billingAddressDifferent ? (values.billingAddress || undefined) : undefined,
                billingCity: values.billingAddressDifferent ? (values.billingCity || undefined) : undefined,
                billingZip: values.billingAddressDifferent ? (values.billingZip || undefined) : undefined,
                billingCountry: values.billingAddressDifferent ? (values.billingCountry || undefined) : undefined,
                companyId: values.companyId || undefined,
                companyVatId: values.companyVatId || undefined,
            }),
        onSuccess: () => {
            toast.success(t("admin.settings.toastSaved"))
            queryClient.invalidateQueries({ queryKey: ["admin", "areals"] })
        },
        onError: (err) => {
            toast.error(err instanceof ApiRequestError ? err.message : t("admin.settings.toastFailed"))
        },
    })

    const geocodeMutation = useMutation({
        mutationFn: async () => {
            const { address, city, country } = getValues()
            const query = [address, city, country].filter(Boolean).join(", ")
            return api.get<{ lat: number; lng: number }>(`/areals/geocode?address=${encodeURIComponent(query)}`)
        },
        onSuccess: (data) => {
            setValue("gpsLat", data.lat.toString(), { shouldDirty: true })
            setValue("gpsLng", data.lng.toString(), { shouldDirty: true })
        },
        onError: (err) => {
            if (err instanceof ApiRequestError && err.message === "Geocoding is not configured") {
                toast.error(t("admin.settings.geocodeNotConfigured"))
            } else if (err instanceof ApiRequestError && err.message === "Address not found") {
                toast.error(t("admin.settings.geocodeNotFound"))
            } else {
                toast.error(t("admin.settings.geocodeFailed"))
            }
        },
    })

    return (
        <div className={sectionClass}>
            <h2 className={sectionHeaderClass}>{t("admin.settings.sectionAddress")}</h2>
            <form onSubmit={handleSubmit((v) => saveMutation.mutate(v))} className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                    <div className="col-span-3">
                        <label className={labelClass}>{t("admin.settings.fieldAddress")}</label>
                        <input {...register("address")} className={inputClass} />
                    </div>
                    <div>
                        <label className={labelClass}>{t("admin.settings.fieldZip")}</label>
                        <input {...register("zip")} maxLength={20} className={inputClass} />
                    </div>
                    <div>
                        <label className={labelClass}>{t("admin.settings.fieldCity")}</label>
                        <input {...register("city")} className={inputClass} />
                    </div>
                    <div>
                        <label className={labelClass}>{t("admin.settings.fieldCountry")}</label>
                        <input {...register("country")} maxLength={2} placeholder="CZ" className={inputClass} />
                    </div>
                    <div className="col-span-1 sm:col-span-1">
                        <label className={labelClass}>{t("admin.settings.fieldGpsLat")}</label>
                        <input {...register("gpsLat")} type="text" inputMode="decimal" placeholder="49.123456" className={inputClass} />
                    </div>
                    <div className="col-span-1 sm:col-span-1">
                        <label className={labelClass}>{t("admin.settings.fieldGpsLng")}</label>
                        <input {...register("gpsLng")} type="text" inputMode="decimal" placeholder="18.123456" className={inputClass} />
                    </div>
                </div>

                {/* Billing block */}
                <div className="pt-2 border-t border-slate-100 space-y-4">
                    <div>
                        <label className={labelClass}>{t("admin.settings.fieldCompanyId")}</label>
                        <input {...register("companyId")} maxLength={20} className={inputClass} />
                    </div>
                    <div>
                        <label className={labelClass}>{t("admin.settings.fieldCompanyVatId")}</label>
                        <input {...register("companyVatId")} maxLength={20} className={inputClass} />
                    </div>
                    <label className="flex items-center gap-2 text-sm font-bold text-slate-700">
                        <input type="checkbox" {...register("billingAddressDifferent")} className="rounded accent-brand" />
                        {t("admin.settings.billingAddressDifferent")}
                    </label>
                    {billingDifferent && (
                        <div className="grid grid-cols-3 gap-4 pl-6 border-l-2 border-brand/30">
                            <div className="col-span-3">
                                <label className={labelClass}>{t("admin.settings.fieldBillingCompanyName")}</label>
                                <input {...register("billingCompanyName")} maxLength={200} className={inputClass} />
                            </div>
                            <div className="col-span-3">
                                <label className={labelClass}>{t("admin.settings.fieldBillingAddress")}</label>
                                <input {...register("billingAddress")} className={inputClass} />
                            </div>
                            <div>
                                <label className={labelClass}>{t("admin.settings.fieldZip")}</label>
                                <input {...register("billingZip")} maxLength={20} className={inputClass} />
                            </div>
                            <div>
                                <label className={labelClass}>{t("admin.settings.fieldCity")}</label>
                                <input {...register("billingCity")} className={inputClass} />
                            </div>
                            <div>
                                <label className={labelClass}>{t("admin.settings.fieldCountry")}</label>
                                <input {...register("billingCountry")} maxLength={2} placeholder="CZ" className={inputClass} />
                            </div>
                        </div>
                    )}
                </div>
                <div className="flex items-center justify-between">
                    <button
                        type="button"
                        disabled={geocodeMutation.isPending}
                        onClick={() => geocodeMutation.mutate()}
                        className="flex items-center gap-2 px-4 py-2 text-xs font-black uppercase tracking-widest text-slate-500 bg-slate-100 rounded-2xl hover:bg-slate-200 transition-colors active:scale-95 disabled:opacity-50"
                    >
                        <MapPin className="size-3.5" />
                        {geocodeMutation.isPending ? "…" : t("admin.settings.geocodeLookup")}
                    </button>
                    <button type="submit" disabled={saveMutation.isPending || !isDirty} className={primaryBtnClass}>
                        <Save className="size-3.5" />
                        {saveMutation.isPending ? t("admin.settings.saving") : t("admin.settings.save")}
                    </button>
                </div>
            </form>
        </div>
    )
}
